import {CloudDownload, Download} from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Tooltip,
  Typography
} from '@mui/material';
import {DerivedImage, Image} from '../../../api/caseApiTypes';
import {useSelectionContext} from './selectionContextContext';
import {useAppDispatch, useAppSelector} from '../../../hooks/useAppRedux';
import {useState} from "react";
import zIndex from "@mui/material/styles/zIndex";
import styles from './DownloadSelectedMediaButton.module.css';
import {downloadFile} from '../../../helpers/DownloadFile';
import {pushToast} from '../../../appSlice';

function downloadImagesLink(images: Image[], target: string) {
  const query = images.map(image => `items=${image.id},${image.activeVersion}`).join('&');
  return '/api/image/batch/download?' + query + '&target=' + target;
}

interface Props {
  images: Image[];
}

export function DownloadMediaButton({images}: Props) {
  const dispatch = useAppDispatch();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | HTMLAnchorElement | null>(null);

  const handleDownload = (target: string) => {
    setIsDownloading(true);
    handleClose();
    downloadFile(downloadImagesLink(images, target),
      `Bulk download.zip`,
      onFail,
      onSuccess);
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const findCommonDerivedImages = (images: Image[]): DerivedImage[] => {
    if (!images.length) return [];

    let commonDerivedImages = images[0].versions[images[0].activeVersion].derivedImages;

    for (let i = 1; i < images.length; i++) {
      const currentImageDerived = images[i].versions[images[i].activeVersion].derivedImages;

      commonDerivedImages = commonDerivedImages.filter(common => {
        return currentImageDerived.some(current => {
          return (
            common.format === current.format &&
            common.dimensionUnit === current.dimensionUnit &&
            common.width === current.width &&
            common.height === current.height
          );
        });
      });

      if (!commonDerivedImages.length) return [];
    }

    return commonDerivedImages;
  }

  const onFail = () => {
    setIsDownloading(false);
    dispatch(
      pushToast({
        message: 'Misslyckades ladda ner bild(er)',
        variant: 'error',
        persist: false,
        preventDuplicate: true,
      }),
    );
  }

  const onSuccess = (fileName: string) => {
    setIsDownloading(false);
    dispatch(
      pushToast({
        message: `Laddade ner bild(er) till '${fileName}'`,
        variant: 'info',
        persist: false,
        preventDuplicate: true,
      }),
    );
  }


  return (
    <>
      <Tooltip title="Högerklicka för att ladda ner fler storlekar">
        <IconButton
          onClick={e => {
            e.preventDefault();
            handleDownload('');
            return false;
          }}
          onContextMenu={e => {
            e.preventDefault();
            handleClick(e);
            return false;
          }}
          size="small"
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="Välj bild att ladda ner"
          aria-haspopup="menu"
          disabled={isDownloading}
        >
          {isDownloading === true ? (
            <CircularProgress size={20} />
          ) : (
            <CloudDownload/>
          )}
        </IconButton>
      </Tooltip>
      <Popover
        id={id}
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        style={{zIndex: zIndex.drawer + 1}}
      >
        <MenuList id="split-button-menu">
          <MenuItem
            className={styles.menuItem}
            component={Button}
            dense
            onClick={e => {
              e.preventDefault();
              handleDownload('');
              return false;
            }}
          >
            <ListItemIcon>
              <Download fontSize="small"/>
            </ListItemIcon>

            <ListItemText disableTypography className={styles.fileDownloadLabels}>
              {'Originalfil'}
            </ListItemText>
          </MenuItem>

          {findCommonDerivedImages(images).map(option => (
            <MenuItem
              onClick={e => {
                e.preventDefault();
                handleDownload(`${option.format}${option.width}x${option.height}`);
                return false;
              }}
              className={styles.menuItem}
              key={option.uri}
              component={Button}
              dense
            >
              <ListItemIcon>
                <Download fontSize="small"/>
              </ListItemIcon>
              <ListItemText
                className={styles.fileDownloadLabels}
              >{`${option.width}x${option.height} ${option.dimensionUnit}`}</ListItemText>
              <Typography variant="body2" color="text.secondary">
                {option.format}
              </Typography>
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </>
  );
}

export function DownloadSelectedMediaButton() {
  const selectionContext = useSelectionContext();
  const selectedImages = useAppSelector(state => state.images.selected[selectionContext]);
  return <DownloadMediaButton images={selectedImages}/>;
}
